// src/components/Profile.js

import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom"; // Obtener el userId de la URL
import PageLayout from "../components/layout/PageLayout";
import ProfileHeader from "./ProfileHeader";
import Description from "./UserDescription";
import UserExperience from "./UserExperience";
import UserEducation from "./UserEducation";
import UserReviews from "./UserReviews";
import { fetchWithAuth, useUser } from "../Context/UserContext";
import ProfileButtons from "./ProfileButtons"; // Importar el componente de botones
import { LoadingOverlay } from "../components/ui/loading-overlay";
import API_URL from "../config";
import { Helmet } from "react-helmet-async";

const Profile = () => {
  const { userId } = useParams(); // Obtener el userId de la URL
  const { user, redirectingToLogin, setRedirectingToLogin } = useUser(); // Obtener los datos del usuario autenticado
  const navigate = useNavigate();
  const location = useLocation(); // Usar useLocation para obtener el estado de navegación
  const [profile, setProfile] = useState(null); // Perfil del usuario visitado
  const [isLoading, setIsLoading] = useState(false);
  const isOwner = parseInt(user?.user?.id_user, 10) === parseInt(userId, 10);
  const [activeTab, setActiveTab] = useState("perfil");

  // Obtener props del estado de navegación
  const { isEditing = false, isModalOpen = false } = location.state || {};

  // Redirigir si no hay un usuario autenticado
  useEffect(() => {
    if (!user && !redirectingToLogin) {
      setRedirectingToLogin(true);
      navigate("/login");
    }
  }, [user, redirectingToLogin]);

  // Cargar el perfil del usuario visitado
  useEffect(() => {
    const fetchProfile = async () => {
      setIsLoading(true);

      try {
        if (isOwner) {
          // Si es el perfil del usuario autenticado, usa los datos del contexto
          setProfile(user);
        } else {
          // Si es el perfil de otro usuario, haz una solicitud al backend
          const response = await fetchWithAuth(`${API_URL}/profile/${userId}`, {
            method: "GET",
          });

          if (response.ok) {
            const data = await response.json();
            setProfile(data);
            console.log("Perfil cargado:", data);
          } else {
            console.error("Error al cargar el perfil");
          }
        }
      } catch (error) {
        console.error("Error al cargar el perfil:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // Función para actualizar el bio del perfil
  const updateProfileBio = (newBio) => {
    setProfile((prevProfile) => ({
      ...prevProfile,
      profile: {
        ...prevProfile.profile,
        bio: newBio,
      },
    }));
  };

  // Función para actualizar la educación del perfil
  const updateProfileEducation = (updatedEducation) => {
    setProfile((prevProfile) => ({
      ...prevProfile,
      education: updatedEducation,
    }));
  };

  // Función para actualizar la experiencia del perfil
  const updateProfileExperience = (updatedExperience) => {
    setProfile((prevProfile) => ({
      ...prevProfile,
      experiences: updatedExperience,
    }));
  };

  if (isLoading || !profile) {
    return <LoadingOverlay />;
  }

  return (
    <PageLayout>
      <Helmet>
        <title>
          {profile?.user?.nombre && profile?.user?.apellido
            ? `${profile.user.nombre} ${profile.user.apellido} - Perfil en Procu`
            : "Perfil de Usuario - Procu"}
        </title>
        <meta
          name="description"
          content={
            profile?.profile?.bio
              ? profile.profile.bio
              : "Consulta el perfil de este usuario en Procu y descubre su experiencia y reseñas."
          }
        />
        <meta
          name="keywords"
          content="perfil, usuario, experiencia, reseñas, proveedores, negocios, Procu, B2B"
        />
      </Helmet>

      <div className="mx-auto max-w-4xl space-y-6 px-4 py-8 sm:px-6">
        <ProfileHeader
          userData={profile}
          isOwner={isOwner}
          initialIsEditing={isEditing} // Pasar props
          initialIsModalOpen={isModalOpen}
        />
        <ProfileButtons isOwner={isOwner} activeTab={activeTab} setActiveTab={setActiveTab} />
        {activeTab === "perfil" && (
          <>
            <Description
              currentDescription={profile.profile?.bio}
              isOwner={isOwner}
              updateProfileBio={updateProfileBio}
            />
            <UserExperience
              experiences={profile.experiences}
              isOwner={isOwner}
              updateProfileExperience={updateProfileExperience}
            />
            <UserEducation
              education={profile.education}
              isOwner={isOwner}
              updateProfileEducation={updateProfileEducation}
            />
          </>
        )}
        {activeTab === "reviews" && <UserReviews userId={userId} isOwner={isOwner} />}
      </div>
    </PageLayout>
  );
};

export default Profile;
